import styles from "./SurveyConfirmation.module.scss";

import { useCallback } from "react";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { env } from "@/config/env";
import { SurveyEvents } from "@/modules/session-survey/consts/surveyEvents";
import { useSessionSurveyContext } from "@/modules/session-survey/context/useSessionSurveyContext";
import { isContinueWithCoachDeclined } from "@/modules/session-survey/utils/isContinueWithCoachDeclined";
import { AppButton } from "@/modules/shared/components/AppButton";
import { useIsMobile } from "@/modules/shared/hooks/useIsMobile";
import { CheckIcon } from "@/modules/shared/svgs/CheckIcon";
import { captureEvent } from "@/modules/shared/utils/posthog";
import {
  DEFAULT_ENTRY_POINT,
  ENTRY_POINTS,
} from "@/modules/tracking/consts/EntryPoints";

export const SurveyConfirmation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // strict: false — ConfirmationDemo mounts this screen under
  // /dev/survey-components, outside /survey/$id; a strict match would throw.
  const { client } = useSearch({ strict: false });
  // The loose read skips /survey/$id's validateSearch, so re-narrow here —
  // a non-validating parent route could pass through an arbitrary value.
  const entryPoint =
    client && ENTRY_POINTS.includes(client) ? client : DEFAULT_ENTRY_POINT;
  const isMobile = useIsMobile();
  const { answers, questions, sessionContext, surveyId } =
    useSessionSurveyContext();
  const {
    coachId: coachIdValue,
    coachName,
    customerId: customerIdValue,
  } = sessionContext;
  const coachId = Number(coachIdValue);
  const customerId = Number(customerIdValue);
  const hasCoachId = !Number.isNaN(coachId);
  const hasCustomerId = !Number.isNaN(customerId);
  const shouldChooseNewCoach =
    isMobile && isContinueWithCoachDeclined(questions, answers);

  const handleChooseNewCoach = useCallback(() => {
    captureEvent(SurveyEvents.ChooseNewCoachClicked, {
      survey_id: surveyId,
      coach_id: coachIdValue,
    });
    window.location.assign(env.VITE_CALADAN_COACH_MATCHING_URL);
  }, [coachIdValue, surveyId]);

  const handleBookAnotherSession = useCallback(() => {
    if (!hasCoachId) return;

    captureEvent(SurveyEvents.BookAnotherSessionClicked, {
      survey_id: surveyId,
      coach_id: coachIdValue,
    });
    navigate({
      to: "/embed/book",
      search: {
        coachId,
        ...(coachName ? { coachName } : {}),
        ...(hasCustomerId ? { customerId } : {}),
        client: entryPoint,
      },
    });
  }, [
    coachId,
    coachIdValue,
    coachName,
    customerId,
    entryPoint,
    hasCoachId,
    hasCustomerId,
    navigate,
    surveyId,
  ]);

  return (
    <section className={styles.container}>
      <CheckIcon className={styles.checkIcon} />
      <h1 className={styles.title}>{t("sessionSurvey.confirmation.title")}</h1>
      <p className={styles.body}>{t("sessionSurvey.confirmation.body")}</p>
      {(shouldChooseNewCoach || hasCoachId) && (
        <div className={styles.actions}>
          {shouldChooseNewCoach && (
            <AppButton variant="critical" onClick={handleChooseNewCoach}>
              {t("sessionSurvey.confirmation.chooseNewCoach")}
            </AppButton>
          )}
          {hasCoachId && (
            <AppButton
              variant={shouldChooseNewCoach ? "outlined" : "critical"}
              onClick={handleBookAnotherSession}
            >
              {t("sessionSurvey.confirmation.bookAnotherSession")}
            </AppButton>
          )}
        </div>
      )}
    </section>
  );
};
